/**
 * YourSubscriptionPage.js
 * Shows the logged-in user's current active yearly magazine subscription status and edition orders.
 * Offers tabbed view switching between Yearly Subscription and Magazine Edition Orders.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle, XCircle, Clock, Calendar, MapPin, Hash, PackageCheck, RotateCcw, AlertCircle } from 'lucide-react';
import { vendorService } from '../../services/api';
import { useToast } from '../../hooks/useToast';

const YourSubscriptionPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('SUBSCRIPTION'); // 'SUBSCRIPTION' | 'ORDERS'
  // AutoPay cancel state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await vendorService.getMyMagazineSubscription();
      if (res.success && res.data) {
        const subExists = Boolean(res.data.has_subscription && res.data.subscription);
        const fetchedOrders = (res.data.orders || []).sort((a, b) => new Date(b.created_at || b.order_date || 0) - new Date(a.created_at || a.order_date || 0));
        setHasSubscription(subExists);
        setSubscription(res.data.subscription);
        setOrders(fetchedOrders);

        // Default tab selection
        if (!subExists && fetchedOrders.length > 0) {
          setActiveTab('ORDERS');
        } else {
          setActiveTab('SUBSCRIPTION');
        }
      } else {
        setError(res.error || 'Failed to load subscription details.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return 'N/A';
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const handleCancelAutoPay = async () => {
    setCancelling(true);
    try {
      const res = await vendorService.cancelMagazineAutoPay();
      if (res.success) {
        toast.success('AutoPay cancelled. Subscription remains active until expiry.');
        setCancelModalOpen(false);
        // Refresh subscription data
        await fetchSubscription();
      } else {
        toast.error(res.error || 'Failed to cancel AutoPay. Please try again.');
      }
    } catch (err) {
      console.error('Cancel AutoPay error:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'COMPLETED': return { label: 'Active', color: 'bg-emerald-50 text-emerald-700 border border-emerald-200', icon: <CheckCircle size={14} /> };
      case 'EXPIRED': return { label: 'Expired', color: 'bg-rose-50 text-rose-700 border border-rose-200', icon: <XCircle size={14} /> };
      case 'PENDING': return { label: 'Pending Verification', color: 'bg-amber-50 text-amber-700 border border-amber-200', icon: <Clock size={14} /> };
      case 'FAILED': return { label: 'Failed', color: 'bg-red-50 text-red-700 border border-red-200', icon: <XCircle size={14} /> };
      default: return { label: status, color: 'bg-gray-100 text-gray-600 border border-gray-200', icon: null };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-red-700 text-sm max-w-xl mx-auto shadow-xs my-8 flex items-center gap-3">
        <XCircle className="text-red-500 flex-shrink-0" size={20} />
        <div>
          <h4 className="font-bold">Error Loading Details</h4>
          <p className="text-xs text-red-600/90 mt-0.5">{error}</p>
        </div>
      </div>
    );
  }

  // No subscription AND no orders at all
  if ((!hasSubscription || !subscription) && orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center px-4">
        <div className="w-16 h-16 bg-[#e0f7f8] rounded-full flex items-center justify-center mb-4">
          <BookOpen size={32} className="text-[#2ab391]" />
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">No Active Subscription or Orders</h2>
        <p className="text-gray-500 mb-6 max-w-sm text-xs sm:text-sm leading-relaxed font-medium">
          You don't have any active TII Magazine subscription or print copy orders yet.
          Subscribe now to get print editions delivered to your doorstep.
        </p>
        <Link
          to="/my-account/magazine/subscribe"
          className="inline-flex items-center gap-2 bg-[#2ab391] text-white px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm hover:bg-[#239c7e] transition-colors shadow-xs hover:shadow-sm active:scale-95 duration-150"
        >
          <BookOpen size={16} />
          Subscribe / Order Now
        </Link>
      </div>
    );
  }

  const subscriptionStatus = subscription ? getStatusConfig(subscription.status) : null;

  return (
    <div className="px-1 sm:px-4 py-2 space-y-6 w-full">
      {/* Page Header */}
      <div className="border-b border-gray-100 pb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
          Magazine Subscriptions & Orders
        </h1>
        <p className="text-gray-500 text-xs sm:text-sm mt-1 font-medium">
          Manage your annual print subscriptions and recent edition orders
        </p>
      </div>

      {/* Top Tab Switcher Options */}
      <div className="inline-flex items-center gap-1 bg-gray-100/90 p-1 rounded-xl border border-gray-200/80 w-full sm:w-auto mb-5 shadow-2xs">
        {hasSubscription && subscription && (
          <button
            type="button"
            onClick={() => setActiveTab('SUBSCRIPTION')}
            className={`flex-1 sm:flex-initial flex items-center justify-center gap-1 sm:gap-1.5 py-1.5 sm:py-2 px-1.5 sm:px-4 rounded-lg font-bold text-[10.5px] xs:text-xs sm:text-xs md:text-sm transition-all cursor-pointer select-none ${
              activeTab === 'SUBSCRIPTION'
                ? 'bg-[#2ab391] text-white shadow-xs'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
            }`}
          >
            <BookOpen size={13} className="flex-shrink-0" />
            <span className="truncate">Yearly Subscription</span>
          </button>
        )}

        <button
          type="button"
          onClick={() => setActiveTab('ORDERS')}
          className={`flex-1 sm:flex-initial flex items-center justify-center gap-1 sm:gap-1.5 py-1.5 sm:py-2 px-1.5 sm:px-4 rounded-lg font-bold text-[10.5px] xs:text-xs sm:text-xs md:text-sm transition-all cursor-pointer select-none ${
            activeTab === 'ORDERS'
              ? 'bg-[#2ab391] text-white shadow-xs'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
          }`}
        >
          <PackageCheck size={13} className="flex-shrink-0" />
          <span className="truncate">
            Magazine Orders {orders.length > 0 && `(${orders.length})`}
          </span>
        </button>
      </div>

      {/* TAB 1: Yearly Subscription Details */}
      {activeTab === 'SUBSCRIPTION' && hasSubscription && subscription && (
        <div className="bg-white border border-gray-200/90 rounded-2xl p-4 sm:p-6 shadow-xs max-w-3xl space-y-4 sm:space-y-5">
          {/* Header Row: Title & Status Badge */}
          <div className="flex flex-row items-center justify-between gap-2 border-b border-gray-100 pb-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                TII Magazine — Annual Plan
              </h2>
              <p className="text-gray-500 text-[11px] sm:text-xs mt-0.5 font-normal truncate">
                Yearly subscription — 4 Print Editions
              </p>
            </div>
            <span className={`inline-flex items-center gap-1 px-2.5 sm:px-3 py-1 rounded-full text-[11px] sm:text-xs font-semibold flex-shrink-0 ${subscriptionStatus.color}`}>
              {subscriptionStatus.icon}
              {subscriptionStatus.label}
            </span>
          </div>

          {/* Details Grid: 2-column on mobile, 4-column on desktop */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
            <div className="bg-gray-50/70 rounded-xl p-2.5 sm:p-3 border border-gray-100">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5 sm:mb-1">Amount Paid</p>
              <p className="text-lg sm:text-xl font-bold text-[#2ab391]">₹{subscription.amount?.toLocaleString('en-IN')}</p>
            </div>

            <div className="bg-gray-50/70 rounded-xl p-2.5 sm:p-3 border border-gray-100">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5 sm:mb-1 flex items-center gap-1">
                <Calendar size={12} /> Start Date
              </p>
              <p className="text-xs sm:text-sm font-semibold text-gray-750">{formatDate(subscription.start_date)}</p>
            </div>

            <div className="bg-gray-50/70 rounded-xl p-2.5 sm:p-3 border border-gray-100">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5 sm:mb-1 flex items-center gap-1">
                <Calendar size={12} /> Valid Until
              </p>
              <p className="text-xs sm:text-sm font-semibold text-gray-750">{formatDate(subscription.end_date)}</p>
            </div>

            <div className="bg-gray-50/70 rounded-xl p-2.5 sm:p-3 border border-gray-100 flex flex-col justify-center">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5 sm:mb-1 flex items-center gap-1">
                <Hash size={12} /> Order ID
              </p>
              <p className="text-xs text-gray-700 font-medium break-all leading-snug select-all" title={subscription.razorpay_order_id}>
                {subscription.razorpay_order_id}
              </p>
            </div>
          </div>

          {/* Delivery Address Box */}
          <div className="bg-gray-50/70 rounded-xl p-3 sm:p-3.5 border border-gray-100 space-y-1">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <MapPin size={13} className="text-[#2ab391]" /> Delivery Address
            </p>
            <p className="text-xs sm:text-sm text-gray-750 font-normal leading-relaxed">
              {subscription.delivery_address}
            </p>
            {subscription.pincode && (
              <span className="inline-block text-[11px] sm:text-xs text-gray-500 font-normal bg-white px-2 py-0.5 rounded-md border border-gray-200 mt-1">
                Pincode: {subscription.pincode}
              </span>
            )}
          </div>

          {/* Subscription Features */}
          <div className="pt-1 border-t border-gray-100">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Subscription Includes</p>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {['4 Print Editions/Year', 'GST Included', 'Delivery Across India'].map((f) => (
                <span key={f} className="flex items-center gap-1 bg-[#e0f7f8] text-[#2ab391] text-[11px] sm:text-xs font-medium px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-[#2ab391]/20">
                  ✓ {f}
                </span>
              ))}
            </div>
          </div>

          {/* AutoPay Status Section — only shown for Our Web AutoPay subscriptions */}
          {subscription.auto_pay_enabled && (
            <div className="border border-gray-100 rounded-xl p-3 sm:p-4 space-y-2">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <RotateCcw size={12} className="text-[#2ab391]" /> AutoPay
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {/* AutoPay status pill */}
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                  subscription.autopay_status === 'ACTIVE'    ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  subscription.autopay_status === 'HALTED'    ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  subscription.autopay_status === 'CANCELLED' ? 'bg-gray-100 text-gray-600 border-gray-200' : 'bg-gray-50 text-gray-500 border-gray-200'
                }`}>
                  AutoPay {subscription.autopay_status}
                </span>
                {/* Next auto-debit hint */}
                {subscription.autopay_status === 'ACTIVE' && subscription.end_date && (
                  <span className="text-xs text-gray-500">
                    Next auto-debit on {formatDate(subscription.end_date)}
                  </span>
                )}
              </div>

              {/* HALTED warning */}
              {subscription.autopay_status === 'HALTED' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex items-start gap-2 mt-2">
                  <AlertCircle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>AutoPay could not be processed. Please check your bank account balance or UPI settings. Your subscription remains active until its expiry date.</span>
                </div>
              )}

              {/* Cancel AutoPay button — only when ACTIVE */}
              {subscription.autopay_status === 'ACTIVE' && (
                <button
                  onClick={() => setCancelModalOpen(true)}
                  className="text-xs text-red-500 hover:text-red-700 underline cursor-pointer mt-1 inline-block"
                >
                  Cancel AutoPay
                </button>
              )}
            </div>
          )}

          {/* Renew CTA for expired/pending */}
          {subscription.status !== 'COMPLETED' && (
            <div className="mt-3 p-3 sm:p-4 rounded-xl border border-dashed flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-50 border-gray-300">
              <div className="text-center sm:text-left">
                <h4 className="text-xs sm:text-sm font-semibold text-gray-800">
                  {subscription.status === 'PENDING' ? 'Verification in Progress' : 'Subscription Inactive'}
                </h4>
                <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5 max-w-sm">
                  {subscription.status === 'PENDING'
                    ? 'We are verifying your transaction. Your subscription will activate shortly.'
                    : 'To continue receiving the TII Print Magazine, please subscribe again.'}
                </p>
              </div>
              {subscription.status !== 'PENDING' && (
                <Link
                  to="/my-account/magazine/subscribe"
                  className="bg-[#2ab391] text-white hover:bg-[#239c7e] text-xs font-bold px-4 py-2 rounded-lg transition-colors whitespace-nowrap shadow-xs cursor-pointer"
                >
                  Renew Subscription
                </Link>
              )}
            </div>
          )}
        </div>
      )}

      {/* TAB 2: Magazine Edition Orders */}
      {activeTab === 'ORDERS' && (
        <div className="space-y-4 max-w-3xl">
          {orders.length === 0 ? (
            <div className="bg-white border border-gray-200/90 rounded-2xl p-8 sm:p-10 text-center text-gray-500 space-y-2 shadow-xs">
              <BookOpen size={32} className="mx-auto text-gray-400 mb-2" />
              <h3 className="font-bold text-base text-gray-800">No Print Copy Orders Found</h3>
              <p className="text-xs text-gray-400">You haven't placed any individual magazine edition orders yet.</p>
            </div>
          ) : (
            orders.map((order) => {
              const payStatus = getStatusConfig(order.status);
              let deliveryBadgeClass = 'bg-amber-50 text-amber-700 border-amber-200';
              if (order.delivery_status === 'Shipped') {
                deliveryBadgeClass = 'bg-blue-50 text-blue-700 border-blue-200';
              } else if (order.delivery_status === 'Delivered') {
                deliveryBadgeClass = 'bg-emerald-50 text-emerald-700 border-emerald-200';
              }

              return (
                <div key={order.id} className="bg-white border border-gray-200/90 rounded-2xl p-4 sm:p-6 shadow-xs hover:border-gray-300 transition-all space-y-4 sm:space-y-5">
                  {/* Header Row: Title & Payment Status Pill */}
                  <div className="flex flex-row items-center justify-between gap-2 border-b border-gray-100 pb-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                        TII Magazine — {order.magazine_month} {order.magazine_year} Edition
                      </h3>
                      <p className="text-gray-500 text-[11px] sm:text-xs mt-0.5 font-normal truncate">
                        Ordered on {formatDate(order.created_at)}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2.5 sm:px-3 py-1 rounded-full text-[11px] sm:text-xs font-semibold flex-shrink-0 ${payStatus.color}`}>
                      {payStatus.icon}
                      {payStatus.label}
                    </span>
                  </div>

                  {/* Details Grid: 2-column on mobile, 4-column on desktop */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
                    {/* 1. Total Amount */}
                    <div className="bg-gray-50/70 rounded-xl p-2.5 sm:p-3 border border-gray-100">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5 sm:mb-1">Amount Paid</p>
                      <p className="text-lg sm:text-xl font-bold text-[#2ab391]">₹{order.total_amount?.toLocaleString('en-IN')}</p>
                    </div>

                    {/* 2. Quantity */}
                    <div className="bg-gray-50/70 rounded-xl p-2.5 sm:p-3 border border-gray-100">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5 sm:mb-1 flex items-center gap-1">
                        <BookOpen size={12} /> Quantity
                      </p>
                      <p className="text-xs sm:text-sm font-semibold text-gray-750">{order.quantity} {order.quantity === 1 ? 'Copy' : 'Copies'}</p>
                    </div>

                    {/* 3. Delivery Status */}
                    <div className="bg-gray-50/70 rounded-xl p-2.5 sm:p-3 border border-gray-100">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5 sm:mb-1 flex items-center gap-1">
                        <Clock size={12} /> Delivery Status
                      </p>
                      <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold border uppercase tracking-wider ${deliveryBadgeClass}`}>
                        {order.delivery_status || 'Pending'}
                      </span>
                    </div>

                    {/* 4. Razorpay Order ID */}
                    <div className="bg-gray-50/70 rounded-xl p-2.5 sm:p-3 border border-gray-100 flex flex-col justify-center">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5 sm:mb-1 flex items-center gap-1">
                        <Hash size={12} /> Order ID
                      </p>
                      <p className="text-xs text-gray-700 font-medium break-all leading-snug select-all" title={order.razorpay_order_id}>
                        {order.razorpay_order_id || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Shipping Address Box */}
                  <div className="bg-gray-50/70 rounded-xl p-3 sm:p-3.5 border border-gray-100 space-y-1">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                      <MapPin size={13} className="text-[#2ab391]" /> Shipping Address
                    </p>
                    <p className="text-xs sm:text-sm text-gray-800 font-medium">
                      <span className="text-gray-900 font-semibold">{order.name}</span> ({order.phone})
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600 font-normal leading-relaxed">
                      {order.delivery_address}
                    </p>
                    {order.pincode && (
                      <span className="inline-block text-[11px] sm:text-xs text-gray-500 font-normal bg-white px-2 py-0.5 rounded-md border border-gray-200 mt-1">
                        Pincode: {order.pincode}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Cancel AutoPay Confirmation Modal */}
      {cancelModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full space-y-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center flex-shrink-0">
                <XCircle size={20} className="text-red-500" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-base">Cancel AutoPay?</h3>
                <p className="text-xs text-gray-500 mt-0.5">This action cannot be undone.</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
              <strong>Your subscription remains active until {formatDate(subscription?.end_date)}.</strong>{' '}
              After that date, it will NOT auto-renew. You can resubscribe manually anytime.
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setCancelModalOpen(false)}
                disabled={cancelling}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Keep AutoPay
              </button>
              <button
                onClick={handleCancelAutoPay}
                disabled={cancelling}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-xs font-bold transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-1"
              >
                {cancelling ? (
                  <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                ) : null}
                Yes, Cancel AutoPay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YourSubscriptionPage;
